import React, { useEffect, useRef } from "react";

const toc = [
  ["Overview & Motivation", "overview"],
  ["Surface Normals — Sphere", "normals-sphere"],
  ["Surface Normals — Triangle", "normals-triangle"],
  ["Surface Normals — AABB", "normals-aabb"],
  ["Direct vs Indirect Illumination", "illumination"],
  ["Global Illumination", "global-illumination"],
  ["Diffuse (Lambertian) Shading", "diffuse"],
  ["Ambient Illumination", "ambient"],
  ["Specular Reflection", "specular"],
  ["Phong Shading", "phong"],
  ["Reflection Vector Derivation", "reflection-vector"],
  ["Blinn-Phong Shading", "blinn-phong"],
  ["Multiple Light Sources", "multiple-lights"],
  ["Distance Attenuation", "attenuation"],
  ["Attenuation + Blinn-Phong", "attenuation-blinn-phong"],
  ["Shading in Ray Tracing", "ray-tracing"],
  ["Diffuse vs Phong Summary", "summary"],
];

const F = ({ children }) => (
  <div className="formula-block">
    {(Array.isArray(children) ? children : [children]).map((line, i) => (
      <div key={i} className="formula-row">{line}</div>
    ))}
  </div>
);

const NoteBox = ({ label = "Note", children }) => (
  <div className="note-box">
    <div className="note-label">{label}</div>
    {children}
  </div>
);

const SurfaceShading = ({ renderToc }) => {
  const ref = useRef(null);

  useEffect(() => {
    const tryRender = () => {
      if (window.renderMathInElement && ref.current) {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
          ],
          throwOnError: false,
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }, []);

  useEffect(() => {
    if (renderToc) renderToc(toc);
  }, [renderToc]);

  return (
    <div ref={ref}>
      <h1>Surface Shading</h1>
      <p className="subtitle">by Jeff Wilson, Ph.D.</p>

      {/* ── OVERVIEW ── */}
      <h2 id="overview">Overview &amp; Motivation</h2>
      <p>
        Surface shading transforms flat geometry into visually compelling scenes
        by simulating how light interacts with surfaces. It enhances the
        perception of depth and material properties in 3D rendering.
      </p>
      <p>
        This lecture focuses on two core shading models applicable to ray tracing:
      </p>
      <ul>
        <li>
          <strong>Diffuse (Lambertian) Shading</strong> — models matte surfaces
          where light scatters evenly in all directions.
        </li>
        <li>
          <strong>Phong Shading</strong> — extends diffuse shading with
          specular highlights for shiny surfaces.
        </li>
      </ul>

      {/* ── SURFACE NORMALS ── */}
      <h2 id="normals-sphere">Surface Normals — Sphere</h2>
      <p>
        A <strong>surface normal</strong> is a unit vector perpendicular to a
        surface at a given point. It defines surface orientation for shading
        and reflection calculations. Normals are almost always unit vectors
        (length = 1).
      </p>
      <p>
        For a sphere centered at {"$\\mathbf{C}$"} with radius {"$r$"}, the
        outward normal at surface point {"$\\mathbf{P}$"} is:
      </p>
      <F>
        {[
          "$$\\mathbf{N} = \\frac{\\mathbf{P}-\\mathbf{C}}{\\|\\mathbf{P}-\\mathbf{C}\\|}$$",
          "$\\mathbf{P}-\\mathbf{C}$ gives the vector from center to surface point.",
          "Normalising removes dependence on radius — $\\mathbf{N}$ always points outward.",
        ]}
      </F>

      <h2 id="normals-triangle">Surface Normals — Triangle</h2>
      <p>
        For a triangle with vertices {"$\\mathbf{V}_1, \\mathbf{V}_2, \\mathbf{V}_3$"},
        compute two edge vectors from a common vertex and take their cross
        product:
      </p>
      <F>
        {[
          "$\\mathbf{E}_1 = \\mathbf{V}_2 - \\mathbf{V}_1 \\qquad \\mathbf{E}_2 = \\mathbf{V}_3 - \\mathbf{V}_1$",
          "$\\mathbf{N} = \\mathbf{E}_1 \\times \\mathbf{E}_2$ (then normalise to unit length)",
        ]}
      </F>
      <p>
        The direction of {"$\\mathbf{N}$"} depends on the winding order of the
        vertices. Counter-clockwise winding (viewed from outside) produces an
        outward-pointing normal. Consistent winding across a mesh is essential
        for correct one-sided surface normals.
      </p>

      <h2 id="normals-aabb">Surface Normals — AABB</h2>
      <p>
        For an axis-aligned bounding box, the hit plane is identified by
        whichever axis last updated {"$t_\\text{near}$"} during the slab test.
        The normal is that axis's unit vector, negated by the sign of the ray
        direction on that axis:
      </p>
      <F>
        {"$$\\mathbf{N} = -\\operatorname{sign}(D_\\text{axis})\\,\\hat{\\mathbf{e}}_\\text{axis}$$"}
      </F>

      {/* ── ILLUMINATION ── */}
      <h2 id="illumination">Direct vs Indirect Illumination</h2>
      <h3>Direct Illumination</h3>
      <p>
        Light that reaches a surface <em>directly</em> from a light source
        (e.g. a spotlight, sunlight). In a basic ray tracer, surfaces not
        directly lit receive no contribution and appear completely black —
        producing unrealistically harsh shadows.
      </p>

      <h3>Indirect Illumination</h3>
      <p>
        Light that reaches a surface only after one or more reflections or
        scattering events (e.g. light bouncing off a red wall tinting nearby
        objects). Indirect illumination is responsible for the soft, ambient
        character of real-world lighting.
      </p>
      <ul>
        <li>Indirect reflections off other surfaces</li>
        <li>
          Color bleeding — e.g. red walls subtly illuminate nearby objects red
        </li>
      </ul>

      {/* ── GLOBAL ILLUMINATION ── */}
      <h2 id="global-illumination">Global Illumination</h2>
      <p>
        <strong>Global illumination</strong> is the comprehensive simulation of
        all light in a scene, including both direct and indirect contributions.
        It captures shadows, subtle light interactions, and realistic
        inter-object reflections that direct-only models miss.
      </p>
      <NoteBox>
        Full global illumination is computationally expensive. Many real-time
        pipelines approximate it using ambient terms, screen-space techniques,
        or pre-computed irradiance.
      </NoteBox>

      {/* ── DIFFUSE SHADING ── */}
      <h2 id="diffuse">Diffuse (Lambertian) Shading</h2>
      <p>
        Diffuse shading models <strong>matte surfaces</strong> (paper,
        unfinished wood) using <strong>Lambert's cosine law</strong>: the
        reflected intensity is proportional to {"$\\cos\\theta$"}, where{" "}
        {"$\\theta$"} is the angle between the surface normal and the light
        direction.
      </p>
      <F>
        {[
          "$$c = c_r \\cdot c_l \\cdot \\max(0,\\,\\mathbf{n}\\cdot\\mathbf{l})$$",
        ]}
      </F>
      <p>Variables:</p>
      <ul>
        <li>
          {"$c_r$"} — surface reflectance (how much light is reflected)
        </li>
        <li>{"$c_l$"} — intensity of the light source</li>
        <li>
          {"$\\mathbf{n}$"} — surface normal (unit vector, perpendicular to surface)
        </li>
        <li>
          {"$\\mathbf{l}$"} — direction toward the light (unit vector)
        </li>
        <li>
          {"$\\max(0, \\mathbf{n}\\cdot\\mathbf{l})$"} — clamps negative values
          (back-facing surfaces receive no contribution)
        </li>
      </ul>
      <p>
        Diffuse shading is <strong>view-independent</strong>: the reflected
        intensity looks the same regardless of where the observer stands.
      </p>
      <p>
        Diffuse shading can model both <strong>direct illumination</strong> (a
        single light direction) and <strong>global illumination</strong> (by
        integrating over all incoming directions, including indirect paths).
      </p>

      {/* ── AMBIENT ── */}
      <h2 id="ambient">Ambient Illumination</h2>
      <p>
        To prevent unlit areas from appearing completely black, add a constant
        baseline term {"$c_a$"} that crudely approximates indirect (global)
        lighting:
      </p>
      <F>
        {[
          "$$c = c_r \\cdot \\bigl(c_a + c_l \\cdot \\max(0,\\,\\mathbf{n}\\cdot\\mathbf{l})\\bigr)$$",
        ]}
      </F>
      <ul>
        <li>{"$c_a$"} — ambient light intensity (constant, scene-wide)</li>
        <li>Keeps shadows soft and visually readable</li>
      </ul>
      <NoteBox label="Limitation">
        Ambient illumination is a crude approximation. It adds a uniform
        constant to every surface — it does not capture the actual direction or
        bounce of indirect light.
      </NoteBox>

      {/* ── SPECULAR ── */}
      <h2 id="specular">Specular Reflection</h2>
      <p>
        Specular reflection occurs when light reflects off a surface in a
        single dominant direction, like a mirror. It produces sharp, bright
        highlights on smooth surfaces (polished metal, glossy plastic).
      </p>
      <p>
        Highlights become visible when the <strong>view direction</strong>{" "}
        {"$\\mathbf{e}$"} aligns with the <strong>reflection direction</strong>{" "}
        {"$\\mathbf{r}$"} of the light source. The sharpness of the highlight is
        controlled by a <em>Phong exponent</em> {"$p$"}: larger {"$p$"} produces
        a tighter, shinier highlight.
      </p>

      {/* ── PHONG ── */}
      <h2 id="phong">Phong Shading</h2>
      <p>
        Phong shading extends diffuse shading with a specular term, modelling
        highlights on shiny surfaces. It is a <strong>direct illumination</strong>{" "}
        method.
      </p>
      <F>
        {[
          "$$c = c_r\\bigl(c_a + c_l\\max(0,\\mathbf{n}\\cdot\\mathbf{l})\\bigr) + c_s\\cdot c_l\\cdot\\max(0,\\mathbf{e}\\cdot\\mathbf{r})^p$$",
        ]}
      </F>
      <p>Additional variables beyond the diffuse model:</p>
      <ul>
        <li>{"$c_s$"} — specular reflectance</li>
        <li>
          {"$\\mathbf{e}$"} — unit vector from the surface toward the eye
        </li>
        <li>
          {"$\\mathbf{r}$"} — reflection of {"$\\mathbf{l}$"} about {"$\\mathbf{n}$"}
        </li>
        <li>
          {"$p$"} — Phong exponent (shininess); higher = sharper highlight
        </li>
      </ul>
      <p>
        Phong shading is <strong>view-dependent</strong>: highlights shift as
        the observer moves, which is essential for convincing shiny surfaces.
      </p>

      {/* ── REFLECTION VECTOR ── */}
      <h2 id="reflection-vector">Reflection Vector Derivation</h2>
      <p>
        The reflection vector {"$\\mathbf{r}$"} is the mirror of the light
        direction {"$\\mathbf{l}$"} about the surface normal {"$\\mathbf{n}$"}.
      </p>
      <h3>Step 1 — Decompose {"$\\mathbf{l}$"}</h3>
      <p>
        Split {"$\\mathbf{l}$"} into components parallel and perpendicular to{" "}
        {"$\\mathbf{n}$"}:
      </p>
      <F>
        {[
          "$\\mathbf{l} = \\mathbf{l}_\\parallel + \\mathbf{l}_\\perp$",
          "$\\mathbf{l}_\\parallel = (\\mathbf{l}\\cdot\\mathbf{n})\\,\\mathbf{n}$ — projection onto $\\mathbf{n}$",
          "$\\mathbf{l}_\\perp = \\mathbf{l} - \\mathbf{l}_\\parallel$ — perpendicular remainder",
        ]}
      </F>
      <h3>Step 2 — Build the reflection</h3>
      <p>
        Reflecting {"$\\mathbf{l}$"} keeps the parallel component unchanged and
        flips the perpendicular component:
      </p>
      <F>
        {[
          "$\\mathbf{r} = \\mathbf{l}_\\parallel - \\mathbf{l}_\\perp$",
          "$= (\\mathbf{l}\\cdot\\mathbf{n})\\mathbf{n} - (\\mathbf{l} - (\\mathbf{l}\\cdot\\mathbf{n})\\mathbf{n})$",
        ]}
      </F>
      <h3>Step 3 — Simplify</h3>
      <F>
        {"$$\\boxed{\\mathbf{r} = -\\mathbf{l} + 2(\\mathbf{l}\\cdot\\mathbf{n})\\,\\mathbf{n}}$$"}
      </F>
      <NoteBox>
        This same formula applies to computing reflection rays in a ray tracer,
        replacing {"$\\mathbf{l}$"} with{"  "}
        {"$\\mathbf{v}=-\\mathbf{D}$"} (the negated eye-ray direction).
      </NoteBox>

      {/* ── BLINN-PHONG ── */}
      <h2 id="blinn-phong">Blinn-Phong Shading</h2>
      <p>
        James Blinn optimised Phong shading by replacing the reflection vector
        with a <strong>halfway vector</strong> {"$\\mathbf{h}$"}, which is
        cheaper to compute and gives a softer, often more aesthetically
        pleasing highlight.
      </p>
      <F>
        {[
          "$$\\mathbf{h} = \\frac{\\mathbf{l}+\\mathbf{e}}{\\|\\mathbf{l}+\\mathbf{e}\\|}$$",
          "$$c = c_r\\bigl(c_a + c_l\\max(0,\\mathbf{n}\\cdot\\mathbf{l})\\bigr) + c_s\\cdot c_l\\cdot\\max(0,\\mathbf{n}\\cdot\\mathbf{h})^p$$",
        ]}
      </F>
      <ul>
        <li>Avoids computing the reflection vector entirely</li>
        <li>
          Specular highlight is softer than Phong — sometimes desirable
        </li>
        <li>
          To match Phong visually: Phong exponent {"$\\approx 4\\times$"} the
          Blinn-Phong exponent
        </li>
      </ul>

      {/* ── MULTIPLE LIGHTS ── */}
      <h2 id="multiple-lights">Multiple Light Sources</h2>
      <p>
        The <strong>superposition principle</strong> lets us sum contributions
        from {"$N$"} independent point lights. Extend Blinn-Phong:
      </p>
      <F>
        {[
          "$$c = c_r\\cdot c_a + \\sum_{i=1}^{N}\\Bigl[c_r\\cdot c_{l_i}\\cdot\\max(0,\\mathbf{n}\\cdot\\mathbf{l}_i) + c_s\\cdot c_{l_i}\\cdot\\max(0,\\mathbf{n}\\cdot\\mathbf{h}_i)^p\\Bigr]$$",
        ]}
      </F>
      <p>
        Each light {"$i$"} contributes its own diffuse and specular terms with
        its own direction {"$\\mathbf{l}_i$"}, halfway vector{" "}
        {"$\\mathbf{h}_i$"}, and intensity {"$c_{l_i}$"}.
      </p>

      {/* ── ATTENUATION ── */}
      <h2 id="attenuation">Distance-Based Light Attenuation</h2>
      <p>
        Real-world light loses intensity with distance. Without attenuation,
        distant lights appear unrealistically strong. Three common models:
      </p>
      <F>
        {[
          "No attenuation: $A = 1$ — good for sunlight (directional)",
          "Linear: $A = \\dfrac{1}{d}$ — simple artificial light sources",
          "Quadratic: $A = \\dfrac{1}{a + bd + cd^2}$ — more physically realistic",
        ]}
      </F>
      <p>
        Here {"$d$"} is the distance from the surface to the light source and{" "}
        {"$a, b, c$"} are tunable coefficients controlling how quickly the
        light fades. This form is widely used in real-time engines (OpenGL,
        Unity).
      </p>

      {/* ── ATTENUATION + BLINN-PHONG ── */}
      <h2 id="attenuation-blinn-phong">Attenuation + Blinn-Phong</h2>
      <p>
        Multiply each light's contribution by its attenuation factor{" "}
        {"$A_i$"} (computed from the distance {"$d_i$"} to that light):
      </p>
      <F>
        {[
          "$$c = c_r\\cdot c_a + \\sum_{i=1}^{N} A_i\\Bigl[c_r\\cdot c_{l_i}\\cdot\\max(0,\\mathbf{n}\\cdot\\mathbf{l}_i)+c_s\\cdot c_{l_i}\\cdot\\max(0,\\mathbf{n}\\cdot\\mathbf{h}_i)^p\\Bigr]$$",
        ]}
      </F>
      <ul>
        <li>Nearby lights have a strong effect; distant ones fade naturally</li>
        <li>
          Choose coefficients to avoid sharp cutoffs or complete blackness
        </li>
        <li>
          Artistic control often outweighs strict physical accuracy — engines
          frequently clamp a minimum ambient level
        </li>
      </ul>

      {/* ── RAY TRACING ── */}
      <h2 id="ray-tracing">Shading in Ray Tracing</h2>
      <p>
        Both diffuse and Phong shading are evaluated at the{" "}
        <strong>ray-object intersection point</strong>. The workflow per pixel:
      </p>
      <ol>
        <li>
          Cast a primary ray from the eye through the pixel
        </li>
        <li>
          Find the closest intersection point {"$\\mathbf{P}$"} and retrieve
          its surface normal {"$\\mathbf{n}$"}
        </li>
        <li>
          For each light, compute {"$\\mathbf{l}_i$"} (direction to light) and
          {"$d_i$"} (distance)
        </li>
        <li>
          Evaluate the shading equation (Diffuse / Phong / Blinn-Phong) to
          produce the pixel colour
        </li>
      </ol>
      <p>
        <strong>Diffuse shading</strong> only needs {"$\\mathbf{n}$"} and{" "}
        {"$\\mathbf{l}$"} — it is view-independent.
      </p>
      <p>
        <strong>Phong / Blinn-Phong</strong> additionally needs the eye
        direction {"$\\mathbf{e}$"} and either the reflection vector{" "}
        {"$\\mathbf{r}$"} (Phong) or the halfway vector {"$\\mathbf{h}$"}{" "}
        (Blinn-Phong) — it is <em>view-dependent</em>.
      </p>

      {/* ── SUMMARY ── */}
      <h2 id="summary">Diffuse vs Phong Summary</h2>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "1rem", fontSize: "0.88rem" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
            <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontFamily: "system-ui", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Property</th>
            <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontFamily: "system-ui", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Diffuse (Lambertian)</th>
            <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontFamily: "system-ui", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Phong / Blinn-Phong</th>
          </tr>
        </thead>
        <tbody>
          {[
            ["Surface type", "Matte (paper, wood)", "Shiny (metal, glass, tiles)"],
            ["View-dependent", "No", "Yes"],
            ["Requires reflection/halfway vec", "No", "Yes"],
            ["Computational cost", "Low", "Higher (+ exponentiation)"],
            ["Specular highlights", "None", "Yes"],
            ["Ambient term", "Optional", "Included"],
            ["Blinn-Phong optimisation", "N/A", "Replaces r with h — softer highlight"],
          ].map(([prop, d, p], i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "8px 12px", fontFamily: "system-ui", fontWeight: 600, color: "#374151" }}>{prop}</td>
              <td style={{ padding: "8px 12px", color: "#374151" }}>{d}</td>
              <td style={{ padding: "8px 12px", color: "#374151" }}>{p}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SurfaceShading;
